import { Button, Text, makeStyles, shorthands } from '@fluentui/react-components';
import { Add16Filled, LineHorizontal116Filled, LinkFilled } from '@fluentui/react-icons';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ISource } from '../../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import { useConversations } from '../../../store/hooks';
import { EllipsisText } from '../../Common/EllipsisText';

interface Props {
    items: ISource[];
    initialVisibleItemCount?: number;
    onSourceClick: (source: ISource) => void;
}

const useStyles = makeStyles({
    source: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        ...shorthands.gap('0.5rem'),
        flexWrap: 'wrap',
    },
    sourceButton: {
        maxWidth: '400px'
    }
});

// renders the list of sources supporting the answer (e.g. for RAG tools)
export const AnswerSources: FC<Props> = ({ items, initialVisibleItemCount = 1, onSourceClick }) => {
    const [visibleItemCount, setVisibleItemCount] = useState(initialVisibleItemCount);
    const visibleItems = items.slice(0, visibleItemCount);
    const showMore = () => setVisibleItemCount(items.length);
    const showLess = () => setVisibleItemCount(initialVisibleItemCount);
    const { t } = useTranslation();
    const classes = useStyles();
    const { activeGpt } = useConversations();

    if (items.length === 0 || activeGpt?.isDefault) {
        return null;
    }

    return (
        <div className={classes.source}>
            <Text weight='medium'>{t('conversation.answer.source', { count: items.length })}</Text>
            {visibleItems?.map((source, index) =>
                // show the text of the source, if it is a website, show the url, otherwise (e.g. a file of the RAG) show the title
                <Button className={classes.sourceButton} icon={<LinkFilled />} key={index} size='small' onClick={() => onSourceClick(source)}>
                    <EllipsisText>{`${1 + index}. `}{source.webSiteUrl ? `${source.webSiteUrl}` : `${source.title}`}</EllipsisText>
                </Button>
            )}
            {visibleItemCount < items.length ? (
                <Button appearance='secondary' size='small' onClick={(_) => showMore()} icon={<Add16Filled />}>
                    {t('conversation.answer.seeMoreSources', { hiddenCount: items.length - visibleItemCount })}
                </Button>
            ) : initialVisibleItemCount >= items.length ? null : (
                <Button appearance='secondary' size='small' onClick={(_) => showLess()} icon={<LineHorizontal116Filled />}>
                    {t('conversation.answer.seeLessSources')}
                </Button>
            )}
        </div>
    );
};
